/**
 * Progressive 50/50 Style2 (Commitment Showdown) — Style-specific sections for GridEngine.
 *
 * hero_section: Dark gradient hero with animated "COMMITMENT SHOWDOWN" title
 * quick_stats: 3-column stat cards (Weekly Cutoff, Rollover Streak, Eliminated)
 * how_it_works: The 52-week "Gauntlet" grid with week tiles (completed/current/upcoming states)
 */
import React, { useState, useEffect } from 'react'
import Clock from 'lucide-react/dist/esm/icons/clock.js';
import Flame from 'lucide-react/dist/esm/icons/flame.js';
import Skull from 'lucide-react/dist/esm/icons/skull.js';
import Trophy from 'lucide-react/dist/esm/icons/trophy.js';
import InlineTranslationEdit from '../../../components/InlineTranslationEdit'
import { useContent } from '../../../contexts/ContentContext'
import { useCountdown } from '../../../hooks/useCountdown'

/**
 * Progressive Style2 Hero Section
 */
export function ProgressiveStyle2HeroSection({ t, c, editMode, handleTranslationUpdate }) {
  const { c: useC } = useContent()
  const ct = c || useC

  return (
    <>
      <h1 className="text-6xl md:text-7xl font-black text-white mb-2 drop-shadow-2xl tracking-tight">
        <InlineTranslationEdit
          translationKey="progressive.commitmentShowdown"
          value={ct('progressive.commitmentShowdown', t?.('progressive.commitmentShowdown'))}
          fallback="COMMITMENT SHOWDOWN"
          onUpdate={handleTranslationUpdate}
          editMode={editMode}
        />
      </h1>
      <p className="text-2xl md:text-3xl text-white/90 font-bold">
        <InlineTranslationEdit
          translationKey="progressive.commitmentSubtitle"
          value={ct('progressive.commitmentSubtitle', t?.('progressive.commitmentSubtitle'))}
          fallback="1 Year Commitment • Stay Active & Win • Miss = Eliminated"
          onUpdate={handleTranslationUpdate}
          editMode={editMode}
        />
      </p>
    </>
  )
}

/**
 * Progressive Style2 Quick Stats Section
 */
export function ProgressiveStyle2QuickStatsSection({ mainDraw, t, c, editMode, handleTranslationUpdate }) {
  const { c: useC } = useContent()
  const ct = c || useC
  const [pulseAnimation, setPulseAnimation] = useState(false)

  const getWeeklyDeadlineDate = () => {
    const now = new Date()
    const nextWeek = new Date(now)
    nextWeek.setDate(now.getDate() + 7)
    return nextWeek
  }

  const { timeLeft: weeklyDeadline } = useCountdown(getWeeklyDeadlineDate())
  const rolloverStreak = mainDraw?.rollover_streak || 0
  const eliminatedThisWeek = mainDraw?.eliminated_this_week || 0

  useEffect(() => {
    if (weeklyDeadline.hours < 24) {
      setPulseAnimation(true)
    }
  }, [weeklyDeadline.hours])

  return (
    <>
      <div className="bg-gradient-to-br from-orange-500 to-red-600 rounded-2xl p-6 border-4 border-yellow-300 shadow-2xl">
        <div className="flex items-center justify-center gap-3 mb-4">
          <Clock className="w-10 h-10 text-white animate-spin-slow" />
          <h3 className="text-2xl font-black text-white uppercase">
            <InlineTranslationEdit
              translationKey="progressive.weeklyCutoff"
              value={ct('progressive.weeklyCutoff', t?.('progressive.weeklyCutoff'))}
              fallback="Weekly Cutoff"
              onUpdate={handleTranslationUpdate}
              editMode={editMode}
            />
          </h3>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <div className="bg-black/30 rounded-lg p-3 text-center backdrop-blur-sm">
            <div className="text-4xl font-black text-white tabular-nums">{String(weeklyDeadline.hours).padStart(2, '0')}</div>
            <div className="text-xs font-bold text-white/80 uppercase">
              <InlineTranslationEdit
                translationKey="progressive.hours"
                value={ct('progressive.hours', t?.('progressive.hours'))}
                fallback="Hours"
                onUpdate={handleTranslationUpdate}
                editMode={editMode}
              />
            </div>
          </div>
          <div className="bg-black/30 rounded-lg p-3 text-center backdrop-blur-sm">
            <div className="text-4xl font-black text-white tabular-nums">{String(weeklyDeadline.minutes).padStart(2, '0')}</div>
            <div className="text-xs font-bold text-white/80 uppercase">
              <InlineTranslationEdit
                translationKey="progressive.mins"
                value={ct('progressive.mins', t?.('progressive.mins'))}
                fallback="Mins"
                onUpdate={handleTranslationUpdate}
                editMode={editMode}
              />
            </div>
          </div>
          <div className="bg-black/30 rounded-lg p-3 text-center backdrop-blur-sm">
            <div className="text-4xl font-black text-white tabular-nums">{String(weeklyDeadline.seconds).padStart(2, '0')}</div>
            <div className="text-xs font-bold text-white/80 uppercase">
              <InlineTranslationEdit
                translationKey="progressive.secs"
                value={ct('progressive.secs', t?.('progressive.secs'))}
                fallback="Secs"
                onUpdate={handleTranslationUpdate}
                editMode={editMode}
              />
            </div>
          </div>
        </div>
        <div className="mt-4 text-center text-sm font-bold text-white/90 uppercase">
          <InlineTranslationEdit
            translationKey="progressive.purchaseWarning"
            value={ct('progressive.purchaseWarning', t?.('progressive.purchaseWarning'))}
            fallback="⚠️ Purchase Now or Be Eliminated! ⚠️"
            onUpdate={handleTranslationUpdate}
            editMode={editMode}
          />
        </div>
      </div>

      <div className="bg-gradient-to-br from-purple-600 to-pink-600 rounded-2xl p-6 border-4 border-white/40 shadow-2xl">
        <div className="flex items-center justify-center gap-3 mb-3">
          <Flame className="w-10 h-10 text-orange-300 animate-bounce" />
          <h3 className="text-2xl font-black text-white uppercase">
            <InlineTranslationEdit
              translationKey="progressive.rolloverStreak"
              value={ct('progressive.rolloverStreak', t?.('progressive.rolloverStreak'))}
              fallback="Rollover Streak"
              onUpdate={handleTranslationUpdate}
              editMode={editMode}
            />
          </h3>
        </div>
        <div className="text-center">
          <div className="text-6xl font-black text-white mb-2 animate-pulse">{rolloverStreak}</div>
          <div className="text-xl font-bold text-white/90 uppercase">
            <InlineTranslationEdit
              translationKey="progressive.consecutiveDraws"
              value={ct('progressive.consecutiveDraws', t?.('progressive.consecutiveDraws'))}
              fallback="Consecutive Draws!"
              onUpdate={handleTranslationUpdate}
              editMode={editMode}
            />
          </div>
          <div className="flex items-center justify-center gap-2 text-lg font-bold text-white mt-3">
            <Flame className="w-5 h-5 text-orange-300" />
            <Flame className="w-5 h-5 text-orange-300" />
            <Flame className="w-5 h-5 text-orange-300" />
          </div>
        </div>
      </div>

      <div className="bg-gradient-to-br from-red-600 to-rose-600 rounded-2xl p-6 border-4 border-white/40 shadow-2xl">
        <div className="flex items-center justify-center gap-3 mb-3">
          <Skull className="w-10 h-10 text-white" />
          <h3 className="text-2xl font-black text-white uppercase">
            <InlineTranslationEdit
              translationKey="progressive.eliminated"
              value={ct('progressive.eliminated', t?.('progressive.eliminated'))}
              fallback="Eliminated"
              onUpdate={handleTranslationUpdate}
              editMode={editMode}
            />
          </h3>
        </div>
        <div className="text-center">
          <div className="text-6xl font-black text-white mb-2">{eliminatedThisWeek}</div>
          <div className="text-lg font-bold text-white/90 uppercase">
            <InlineTranslationEdit
              translationKey="progressive.playersRecently"
              value={ct('progressive.playersRecently', t?.('progressive.playersRecently'))}
              fallback="Players Recently"
              onUpdate={handleTranslationUpdate}
              editMode={editMode}
            />
          </div>
          <div className="mt-3 text-sm font-bold text-white/80">
            <InlineTranslationEdit
              translationKey="progressive.dontBeNext"
              value={ct('progressive.dontBeNext', t?.('progressive.dontBeNext'))}
              fallback="Don't be next!"
              onUpdate={handleTranslationUpdate}
              editMode={editMode}
            />
          </div>
        </div>
      </div>
    </>
  )
}

/**
 * Progressive Style2 How It Works Section — "The Gauntlet" 52-week grid
 */
export function ProgressiveStyle2HowItWorksSection({ mainDraw, t, c, editMode, handleTranslationUpdate }) {
  const { c: useC } = useContent()
  const ct = c || useC

  const currentWeek = mainDraw?.current_week || 1
  const totalWeeks = 52
  const progressPercentage = (currentWeek / totalWeeks) * 100
  const monthsElapsed = Math.floor(currentWeek / 4.33)

  const weekTiles = Array.from({ length: totalWeeks }, (_, i) => i + 1)

  const getWeekStatus = (week) => {
    if (week < currentWeek) return 'completed'
    if (week === currentWeek) return 'current'
    return 'upcoming'
  }

  const renderWeekTile = (week) => {
    const status = getWeekStatus(week)

    const tileStyle = status === 'completed'
      ? { background: 'var(--charity-primary)', color: 'var(--charity-primary-contrast, #fff)' }
      : status === 'upcoming'
        ? { background: 'color-mix(in oklab, var(--charity-primary) 12%, transparent)' }
        : undefined

    return (
      <div
        key={week}
        style={tileStyle}
        className={`
          relative flex items-center justify-center w-12 h-12 rounded-lg font-black text-sm transition-all duration-300
          ${status === 'completed' ? 'shadow-lg scale-105' : ''}
          ${status === 'current' ? 'bg-gradient-to-br from-orange-500 to-red-600 text-white animate-pulse shadow-2xl scale-110 ring-4 ring-yellow-300' : ''}
          ${status === 'upcoming' ? 'text-gray-500 dark:text-gray-300' : ''}
        `}
      >
        {status === 'completed' && <span className="text-2xl">✓</span>}
        {status !== 'completed' && week}
        {status === 'current' && (
          <div className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full animate-ping"></div>
        )}
      </div>
    )
  }

  return (
    <>
      <div className="flex items-center justify-center gap-4 mb-6">
        <Trophy className="w-12 h-12 text-yellow-300" />
        <h2 className="text-4xl font-black text-white uppercase">
          <InlineTranslationEdit
            translationKey="progressive.theGauntlet"
            value={ct('progressive.theGauntlet', t?.('progressive.theGauntlet'))}
            fallback="The Gauntlet"
            onUpdate={handleTranslationUpdate}
            editMode={editMode}
          />
        </h2>
        <Trophy className="w-12 h-12 text-yellow-300" />
      </div>

      <div className="mb-6 text-center">
        <div className="text-6xl md:text-7xl font-black text-white mb-2 drop-shadow-2xl">
          ${(mainDraw?.current_jackpot || 125000).toLocaleString()}
        </div>
        <div className="text-2xl font-bold text-white/90">
          <InlineTranslationEdit
            translationKey="progressive.growingEveryRollover"
            value={ct('progressive.growingEveryRollover', t?.('progressive.growingEveryRollover'))}
            fallback="Growing with every rollover!"
            onUpdate={handleTranslationUpdate}
            editMode={editMode}
          />
        </div>
      </div>

      <div className="grid grid-cols-4 sm:grid-cols-8 md:grid-cols-13 gap-2 mb-6">
        {weekTiles.slice(0, 26).map((week) => renderWeekTile(week))}
      </div>
      <div className="grid grid-cols-4 sm:grid-cols-8 md:grid-cols-13 gap-2">
        {weekTiles.slice(26).map((week) => renderWeekTile(week))}
      </div>

      <div className="mt-6 grid grid-cols-3 gap-4">
        <div className="bg-white/20 backdrop-blur-sm rounded-xl p-4 text-center border-2 border-white/40">
          <div className="text-3xl font-black text-white mb-1">{monthsElapsed}</div>
          <div className="text-sm font-bold text-white/90 uppercase">
            <InlineTranslationEdit
              translationKey="progressive.monthsIn"
              value={ct('progressive.monthsIn', t?.('progressive.monthsIn'))}
              fallback="Months In"
              onUpdate={handleTranslationUpdate}
              editMode={editMode}
            />
          </div>
        </div>
        <div className="bg-white/20 backdrop-blur-sm rounded-xl p-4 text-center border-2 border-white/40">
          <div className="text-3xl font-black text-white mb-1">{12 - monthsElapsed}</div>
          <div className="text-sm font-bold text-white/90 uppercase">
            <InlineTranslationEdit
              translationKey="progressive.monthsLeft"
              value={ct('progressive.monthsLeft', t?.('progressive.monthsLeft'))}
              fallback="Months Left"
              onUpdate={handleTranslationUpdate}
              editMode={editMode}
            />
          </div>
        </div>
        <div className="bg-white/20 backdrop-blur-sm rounded-xl p-4 text-center border-2 border-white/40">
          <div className="text-3xl font-black text-white mb-1">{Math.round(progressPercentage)}%</div>
          <div className="text-sm font-bold text-white/90 uppercase">
            <InlineTranslationEdit
              translationKey="progressive.complete"
              value={ct('progressive.complete', t?.('progressive.complete'))}
              fallback="Complete"
              onUpdate={handleTranslationUpdate}
              editMode={editMode}
            />
          </div>
        </div>
      </div>
    </>
  )
}
